
import { TriangleAlert, Upload } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import Modal from '@/components/Common/Modal';
import { Button } from '@/components/Common/button';
import { ConfigFile } from './types';

interface UploadConfigModalProps {
  openFileId: string | null;
  configFiles: ConfigFile[];
  showConfirmation: boolean;
  onClose: () => void;
  onSelectFile: (fileId: string, file: File | null) => void;
  onContinue: (fileId: string) => void;
  onConfirmReplace: (fileId: string) => void;
  onBackToSelection: () => void;
}

export function UploadConfigModal({
  openFileId,
  configFiles,
  showConfirmation,
  onClose,
  onSelectFile,
  onContinue,
  onConfirmReplace,
  onBackToSelection,
}: UploadConfigModalProps) {
  const { t } = useTranslation();

  if (!openFileId) return null;

  const currentFile = configFiles.find((file) => file.id === openFileId);
  if (!currentFile) return null;

  return (
    <Modal
      open
      title={t('knowledgeManagement.uploadModal.title', { name: t(currentFile.titleKey) })}
      onClose={onClose}
      className="max-w-lg"
      hideDefaultFooter
    >
      <div className="space-y-4">
        {!showConfirmation ? (
          <>
            <p className="text-sm text-muted-foreground">
              <Trans
                i18nKey="knowledgeManagement.uploadModal.selectPrompt"
                values={{ filename: currentFile.filename }}
                components={{ code: <code className="bg-muted px-1.5 py-0.5 rounded text-xs" /> }}
              />
            </p>

            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input
                type="file"
                id={`modal-file-${openFileId}`}
                accept={currentFile.accept}
                onChange={(e) => onSelectFile(openFileId, e.target.files?.[0] || null)}
                className="hidden"
              />
              <label htmlFor={`modal-file-${openFileId}`} className="cursor-pointer">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-sm font-medium text-foreground mb-1">
                  {currentFile.file ? currentFile.file.name : t('knowledgeManagement.uploadModal.clickToSelect')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('knowledgeManagement.uploadModal.format', { format: currentFile.format })}
                </p>
              </label>
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t border-border">
              <Button onClick={onClose} variant="outline">
                {t('actions.cancel')}
              </Button>
              <Button
                onClick={() => onContinue(openFileId)}
                disabled={!currentFile.file || currentFile.loading}
              >
                {currentFile.loading
                  ? t('knowledgeManagement.uploadModal.uploading')
                  : t('knowledgeManagement.uploadModal.continue')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <TriangleAlert className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                    {t('knowledgeManagement.uploadModal.confirmTitle')}
                  </h4>
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <Trans
                      i18nKey="knowledgeManagement.uploadModal.confirmMessage"
                      values={{ filename: currentFile.filename }}
                      components={{ strong: <strong /> }}
                    />
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t border-border">
              <Button onClick={onBackToSelection} variant="outline">
                {t('global.back')}
              </Button>
              <Button
                onClick={() => onConfirmReplace(openFileId)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {t('knowledgeManagement.uploadModal.confirmReplace')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
